import React from "react";
import "./styles.css";
import Barcode from "react-barcode";
import donorsQuery from "../../../utils/queries/donorsQuery";
import { getStorage } from "../../../utils/storage";
import toastMessage from "../../../utils/toastMessage";
import { Loading } from "../../Loader";
import language from "../../../language";
import { connect } from "react-redux";
import RBCLOGO from "../../../assets/rbc-logo.png";
import { PrintComponent } from "../../Print";
import moment from "moment";

// Header accent color per blood group
const BG_COLORS = {
  "A+":  { bg: "#c47f00", text: "#fff" },
  "A-":  { bg: "#c47f00", text: "#fff" },
  "AB+": { bg: "#8e0045", text: "#fff" },
  "AB-": { bg: "#8e0045", text: "#fff" },
  "B+":  { bg: "#1255a1", text: "#fff" },
  "B-":  { bg: "#1255a1", text: "#fff" },
  "O+":  { bg: "#1255a1", text: "#fff" },
  "O-":  { bg: "#1255a1", text: "#fff" },
};
const DEFAULT_ACCENT = { bg: "#c8102e", text: "#fff" };

function getAccent(bloodGroup) {
  if (!bloodGroup) return DEFAULT_ACCENT;
  const base = bloodGroup.replace(/\s*Du\+.*/i, "").trim().toUpperCase();
  return BG_COLORS[base] || DEFAULT_ACCENT;
}

// O- → black badge; all others → white + black border (matches production label)
function getBadgeStyle(bloodGroup) {
  if (!bloodGroup) return { background: "#fff", color: "#000", border: "2px solid #000" };
  const base = bloodGroup.replace(/\s*Du\+.*/i, "").trim().toUpperCase();
  if (base === "O-") return { background: "#000", color: "#fff", border: "none" };
  return { background: "#fff", color: "#000", border: "2px solid #000" };
}

class DonorBadge extends React.Component {
  state = { data: {}, isLoading: false };

  componentDidMount = async () => {
    const user = await getStorage();
    this.setState({ user }, () => this.getData(true));
  };

  getData = async (isLoading) => {
    try {
      const { defaultLanguage } = this.props;
      this.setState({ isLoading });
      const data = await donorsQuery(defaultLanguage, { id: this.props.donorId });
      const res = data?.length > 0 ? data[0] : {};
      this.setState({ data: res, isLoading: false });
    } catch (error) {
      this.setState({ isLoading: false });
      toastMessage("error", error);
    }
  };

  renderBloodGroup(bg) {
    if (!bg || bg === "unspecified") return "—";
    const duIdx = bg.indexOf(" Du+");
    if (duIdx === -1) return bg;
    return (
      <>
        {bg.slice(0, duIdx)}
        <sup style={{ fontSize: "0.6em" }}>Du+</sup>
      </>
    );
  }

  render() {
    const { data, isLoading } = this.state;
    const { defaultLanguage } = this.props;

    const fullName =
      [data.firstName, data.lastName].filter(Boolean).join(" ").toUpperCase() || "—";
    const accent = getAccent(data.bloodGroup);
    const donorNum = data.donorNumber ? String(data.donorNumber) : null;
    const dob = data.dateOfBirth && moment(data.dateOfBirth).isValid()
      ? moment(data.dateOfBirth).format("DD/MM/YYYY")
      : data.dateOfBirth || "—";

    const isUnspecified =
      !data.bloodGroup || data.bloodGroup.toUpperCase() === "UNSPECIFIED";

    return (
      <div className="donor-badge-container">
        {isLoading ? (
          <center><Loading /></center>
        ) : isUnspecified ? (
          <div style={{ textAlign: "center", padding: "2rem 1rem", color: "#c0392b" }}>
            <i className="bx bx-error-circle" style={{ fontSize: "3rem", marginBottom: "0.5rem", display: "block" }} />
            <p style={{ fontWeight: 600, fontSize: "1rem", margin: 0 }}>
              Sorry, some important information are missing.
            </p>
            <p style={{ fontSize: "0.85rem", color: "#555", marginTop: "0.4rem" }}>
              The blood group has not been determined yet. The ID card cannot be printed until this information is available.
            </p>
          </div>
        ) : (
          <PrintComponent buttonText={language[defaultLanguage].print_card}>
            <div className="donor-badge-card-container mb-3">

              {/* ── HEADER ── */}
              <div className="dbc-header">
                <div className="dbc-header-logo-area">
                  <img src={RBCLOGO} alt="RBC" className="dbc-header-logo" />
                  <div className="dbc-header-org">
                    <span className="org-main">Rwanda Biomedical Centre</span>
                    <span className="org-sub">Blood Transfusion Division</span>
                  </div>
                </div>
                <div className="dbc-header-title" style={{ background: accent.bg, color: accent.text }}>
                  <span>BLOOD</span>
                  <span>DONOR ID</span>
                </div>
              </div>

              {/* ── BODY: fields (left) + barcode (right) ── */}
              <div className="dbc-body">
                <div className="dbc-body-left">

                  <div className="dbc-field-row">
                    <span className="dbc-field-label">
                      {language[defaultLanguage].name || "Name"}
                    </span>
                    <span className="dbc-field-value">{fullName}</span>
                  </div>

                  {data.dateOfBirth && (
                    <div className="dbc-field-row">
                      <span className="dbc-field-label">
                        {language[defaultLanguage].date_of_birth || "Date of birth"}
                      </span>
                      <span className="dbc-field-value">{dob}</span>
                    </div>
                  )}

                  <div className="dbc-field-row">
                    <span className="dbc-field-label">
                      {language[defaultLanguage].blood_group || "Blood group"}
                    </span>
                    <div className="dbc-bg-badge" style={getBadgeStyle(data.bloodGroup)}>
                      {this.renderBloodGroup(data.bloodGroup)}
                    </div>
                  </div>

                  <div className="dbc-field-row">
                    <span className="dbc-field-label">
                      {language[defaultLanguage].donor_id || "Donor ID"}
                    </span>
                    <span className="dbc-field-value">{donorNum || "—"}</span>
                  </div>

                </div>

                {/* Barcode on the right */}
                {donorNum && (
                  <div className="dbc-body-right">
                    <Barcode
                      value={donorNum}
                      height={55}
                      width={1.2}
                      fontSize={9}
                      margin={0}
                      background="transparent"
                    />
                  </div>
                )}
              </div>

              {/* ── SLOGAN ── */}
              <div className="dbc-slogan" style={{ borderColor: accent.bg }}>
                <span style={{ color: accent.bg }}>Proud to be a Blood Donor</span>
              </div>

              {/* ── FOOTER ── */}
              <div className="dbc-footer">
                <div className="dbc-contact-col">
                  <span className="dbc-found-text">If this card is found please return to:</span>
                  <span>www.rbc.gov.rw</span>
                  <span>info@rbc.gov.rw</span>
                  <span>KG 644 St, P.O.Box 7162 Kigali</span>
                </div>
                <div className="dbc-divider" />
                <div className="dbc-contact-col right">
                  <span className="dbc-found-text">Utoraguye iyi karita wayisubiza kuri:</span>
                  <span>www.bbms.moh.gov.rw</span>
                  <span>info.btd@rbc.gov.rw</span>
                  <span className="dbc-hotline">Hotline: 114 / 912</span>
                </div>
              </div>

            </div>
          </PrintComponent>
        )}
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return { defaultLanguage };
};

export default connect(mapStateToProps)(DonorBadge);
